"use client";

import { sum } from "es-toolkit";
import { Label, Pie, PieChart } from "recharts";

import type { ChartConfig } from "@/components/ui/chart";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { formatUSD } from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";

export const description = "A donut chart with text";

const chartConfig = {
  usdc: {
    label: "USDC",
    color: "var(--muted-foreground)",
  },
  deso: {
    label: "DESO",
    color: "var(--card)",
  },
  focus: {
    label: "FOCUS",
    color: "var(--primary)",
  },
  focusLocked: {
    label: "FOCUS(LOCKED)",
    color: "var(--primary-foreground)",
  },
} satisfies ChartConfig;

interface WalletPieChartProps {
  desoBalanceUsd: number;
  dusdBalanceUsd: number;
  focusUnlockedBalanceUsd: number;
  focusLockedBalanceUsd: number;
}

export const WalletPieChart = ({
  desoBalanceUsd,
  dusdBalanceUsd,
  focusUnlockedBalanceUsd,
  focusLockedBalanceUsd,
}: WalletPieChartProps) => {
  const chartData = [
    { asset: "usdc", balance: dusdBalanceUsd, fill: "var(--muted-foreground)" },
    {
      asset: "deso",
      balance: desoBalanceUsd,
      fill: "var(--muted)",
    },
    {
      asset: "focus",
      balance: focusUnlockedBalanceUsd,
      fill: "var(--primary)",
    },
    {
      asset: "focusLocked",
      balance: focusLockedBalanceUsd,
      fill: "var(--primary-foreground)",
    },
  ];

  const totalBalanceUsd = sum([
    desoBalanceUsd,
    focusUnlockedBalanceUsd,
    focusLockedBalanceUsd,
    dusdBalanceUsd,
  ]);

  return (
    <ChartContainer
      config={chartConfig}
      className="
        mx-auto aspect-square size-[240px] max-h-[250px]
        lg:size-auto
      "
    >
      <PieChart>
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              hideLabel
              formatter={(value, name) => {
                return `${String(name).toUpperCase()}: ${Number(value) >= 1000 ? `$${abbreviateNumber(Number(value))}` : formatUSD(Number(value))}`;
              }}
            />
          }
        />
        <ChartLegend
          content={<ChartLegendContent nameKey="asset" />}
          className="
            -translate-y-2 flex-wrap gap-2 pt-0
            *:basis-1/4 *:justify-center
          "
        />

        <Pie
          data={chartData}
          dataKey="balance"
          nameKey="asset"
          innerRadius={60}
          strokeWidth={5}
          isAnimationActive={true}
        >
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className="fill-foreground font-mono text-2xl"
                    >
                      {totalBalanceUsd >= 1000
                        ? `$${abbreviateNumber(totalBalanceUsd, {
                            maximumFractionDigits: 0,
                          })}`
                        : formatUSD(totalBalanceUsd)}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 24}
                      className="fill-muted"
                    >
                      Balance
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  );
};
